import type Button from "../../../src/models/Button";
import type Consumer from "../../../src/models/Consumer";
import type Room from "../../../src/models/Room";
import type { C1_Floor } from "./C1_Floor";
import C1_EloItem from "./C1_EloItem";
import { resolveInteractiveEloPosition } from "./C1_interactiveEloPlacement";
import type { C1_Furniture } from "./C1_Furniture";
import type { C1_Opening } from "./C1_Opening";

export type UnplacedElectricalRoom = { room: Room; devices: Array<Button | Consumer> };

function hasPoint(point: { x: number; y: number } | undefined): boolean {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

/** Checks persisted placement, including automatic placement within furniture and openings. */
function isPlaced(device: Button | Consumer, room: Room): boolean {
  if (!hasPoint(room.c1?.data.startPoint)) return false;
  const facet = device.c1;
  if (!(facet instanceof C1_EloItem)) return false;
  if (facet.interactivCreated) {
    return device.parent === room && !!resolveInteractiveEloPosition(facet.data.interactivePlacement, room.c1?.innerRect());
  }
  if (facet.position ? !hasPoint(facet.position) : !facet.definition) return false;

  let parent = device.parent;
  while (parent && parent !== room) {
    const ownerFacet = parent.c1;
    if (ownerFacet?.className === "C1_Furniture") {
      return hasPoint((ownerFacet as C1_Furniture).relativePosition);
    }
    if (ownerFacet?.className === "C1_Opening") {
      const opening = ownerFacet as C1_Opening;
      return Number.isFinite(opening.data.offset) && !!opening.data.side;
    }
    if (!(ownerFacet instanceof C1_EloItem)) return false;
    if (ownerFacet.position && !hasPoint(ownerFacet.position)) return false;
    parent = parent.parent;
  }
  return parent === room;
}

/** Uses the ESTADA collections so devices without C1 facets are included as well. */
export function unplacedElectricalDevicesForRoom(room: Room): Array<Button | Consumer> {
  return [...room.allButtons, ...room.allConsumers()].filter(device => !isPlaced(device, room));
}

export function unplacedElectricalDevicesForFloor(floor: C1_Floor | undefined): UnplacedElectricalRoom[] {
  return floor?.owner?.rooms.asArray().flatMap(room => {
    const devices = unplacedElectricalDevicesForRoom(room);
    return devices.length ? [{ room, devices }] : [];
  }) ?? [];
}
